import { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import {
  CalendarBlankIcon,
  CheckCircleIcon,
  CircleIcon,
  GiftIcon,
  SmileySadIcon,
  SpinnerGapIcon,
  StorefrontIcon,
  WarningCircleIcon,
} from '@phosphor-icons/react';
import { useToaster } from '../../../shared/ui';
import { rewardService } from '../../../shared/utils';
import type { Coupon, RewardRule } from '../../../shared/utils/rewardTypes';
import { Breadcrumb } from '../components/Breadcrumb';
import { formatCouponValue, formatDate } from '../utils/format';
import './RewardsPage.css';

interface RewardsPageProps {
  paymentId?: string;
}

export const RewardsPage = ({ paymentId }: RewardsPageProps) => {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const { showToast } = useToaster();
  const [loading, setLoading] = useState(true);
  const [claiming, setClaiming] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [eligible, setEligible] = useState(false);
  const [rule, setRule] = useState<RewardRule | null>(null);
  const [availableCoupons, setAvailableCoupons] = useState<Coupon[]>([]);
  const [selectedCoupons, setSelectedCoupons] = useState<string[]>([]);
  const [message, setMessage] = useState('');

  useEffect(() => {
    const checkEligibility = async () => {
      if (!paymentId) {
        setError(t('rewardsPage.errorNoPaymentId'));
        setLoading(false);
        return;
      }

      try {
        const response = await rewardService.checkEligibility(paymentId);

        if (response.success && response.data) {
          setEligible(response.data.eligible);
          setRule(response.data.rule || null);
          setAvailableCoupons(response.data.availableCoupons);
          setMessage(response.data.message);
        } else {
          setError(response.error || t('rewardsPage.errorCheckEligibility'));
        }
      } catch (err) {
        const errorMessage =
          err instanceof Error ? err.message : 'An error occurred';
        setError(errorMessage);
      } finally {
        setLoading(false);
      }
    };

    checkEligibility();
  }, [paymentId, t]);

  const handleCouponSelect = (couponId: string) => {
    const maxSelections = rule?.maxSelectionsAllowed || 1;

    setSelectedCoupons(prev => {
      if (prev.includes(couponId)) {
        return prev.filter(id => id !== couponId);
      }

      if (prev.length >= maxSelections) {
        showToast(
          t('rewardsPage.maxSelectionsWarning', { count: maxSelections }),
          'warning'
        );
        return prev;
      }

      return [...prev, couponId];
    });
  };

  const handleClaimRewards = useCallback(async () => {
    if (!paymentId || !rule || selectedCoupons.length === 0) {
      showToast(t('rewardsPage.selectAtLeastOne'), 'warning');
      return;
    }

    setClaiming(true);

    try {
      const response = await rewardService.claimRewards({
        paymentId,
        ruleId: rule.id,
        couponIds: selectedCoupons,
      });

      if (response.success) {
        showToast(t('rewardsPage.claimSuccess'), 'success');
        navigate({ to: '/rewards/history' });
      } else {
        showToast(response.error || t('rewardsPage.claimFailed'), 'error');
      }
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : 'An error occurred';
      showToast(errorMessage, 'error');
    } finally {
      setClaiming(false);
    }
  }, [paymentId, rule, selectedCoupons, showToast, t, navigate]);

  const lang = i18n.language;
  const maxSelections = rule?.maxSelectionsAllowed || 1;
  const breadcrumb = (
    <Breadcrumb
      items={[
        { label: t('app.nav.rewards'), href: '/rewards/history' },
        { label: t('rewardsPage.breadcrumb') },
      ]}
    />
  );

  if (loading) {
    return (
      <>
        {breadcrumb}
        <section className="mc-card mc-state">
          <div className="mc-state__icon">
            <SpinnerGapIcon size={46} weight="bold" className="mc-spin" />
          </div>
          <h2>{t('rewardsPage.loadingTitle')}</h2>
          <p>{t('rewardsPage.loadingMessage')}</p>
        </section>
      </>
    );
  }

  if (error || !eligible) {
    return (
      <>
        {breadcrumb}
        <section className="mc-card mc-state">
          <div className="mc-state__icon mc-state__icon--danger">
            {error ? (
              <WarningCircleIcon size={46} weight="fill" />
            ) : (
              <SmileySadIcon size={46} weight="fill" />
            )}
          </div>
          <h2>
            {error
              ? t('rewardsPage.errorTitle')
              : t('rewardsPage.notEligibleTitle')}
          </h2>
          <p>{error || message || t('rewardsPage.notEligibleMessage')}</p>
          <div className="mc-actions">
            <Link to="/" className="mc-btn mc-btn--outline">
              {t('rewardsPage.goToDashboard')}
            </Link>
            {!error && (
              <Link to="/rewards/history" className="mc-btn mc-btn--primary">
                {t('rewardsPage.viewRewardHistory')}
              </Link>
            )}
          </div>
        </section>
      </>
    );
  }

  return (
    <>
      {breadcrumb}

      <div className="mc-heading">
        <div>
          <h1>{t('rewardsPage.title')}</h1>
          <p>
            {message ||
              t('rewardsPage.congratulationsMessage', { count: maxSelections })}
          </p>
        </div>
        {rule && <span className="mc-tag mc-tag--pending">{rule.name}</span>}
      </div>

      <div className="mc-grid mc-cols3">
        {availableCoupons.map(coupon => {
          const isSelected = selectedCoupons.includes(coupon.id);
          return (
            <button
              key={coupon.id}
              type="button"
              className={`mc-card choose-reward ${isSelected ? 'choose-reward--selected' : ''}`}
              onClick={() => handleCouponSelect(coupon.id)}
              aria-pressed={isSelected}
            >
              <div className="choose-reward__top">
                <span className="mc-discount">
                  {formatCouponValue(coupon, t)}
                </span>
                <span className="choose-reward__check">
                  {isSelected ? (
                    <CheckCircleIcon size={26} weight="fill" />
                  ) : (
                    <CircleIcon size={26} />
                  )}
                </span>
              </div>
              <h3>{coupon.name}</h3>
              {coupon.description && (
                <p className="mc-muted">{coupon.description}</p>
              )}
              <div className="choose-reward__meta">
                {coupon.merchantName && (
                  <span>
                    <StorefrontIcon size={16} />
                    {coupon.merchantName}
                  </span>
                )}
                <span>
                  <CalendarBlankIcon size={16} />
                  {t('rewardsPage.validUntil', {
                    date: formatDate(coupon.validUntil, lang),
                  })}
                </span>
              </div>
              <span
                className={`mc-tag ${isSelected ? 'mc-tag--success' : 'mc-tag--pending'}`}
              >
                {isSelected
                  ? t('rewardsPage.selected')
                  : t('rewardsPage.select')}
              </span>
            </button>
          );
        })}
      </div>

      <section className="mc-card choose-reward__footer">
        <div className="choose-reward__count">
          <GiftIcon size={22} weight="duotone" />
          {t('rewardsPage.selectionCounter', {
            selected: selectedCoupons.length,
            max: maxSelections,
          })}
        </div>
        <div className="mc-actions">
          <Link to="/" className="mc-btn mc-btn--outline">
            {t('rewardsPage.skipForNow')}
          </Link>
          <button
            type="button"
            className="mc-btn mc-btn--primary"
            onClick={handleClaimRewards}
            disabled={selectedCoupons.length === 0 || claiming}
          >
            {claiming ? t('app.loading') : t('rewardsPage.claimRewards')}
          </button>
        </div>
      </section>
    </>
  );
};
